/* eslint-disable no-unused-vars */
import { v4 as uuid } from 'uuid'
import {
  cookies,
  s3Client,
  API,
  parseToken,
  saveCustomTokenKey,
  setRefreshFailureHandler,
  setHttpBaseUrl
} from '@neurotrack/web-core'
import router from '@/router'
import LogRocketUtil from '@/util/logrocket'
import { Analytics } from '../util/env'
import { version } from '../../package.json'

const INIT = 'INIT'
const INIT_USER = 'INIT_USER'
const SET_PROFILE = 'SET_PROFILE'
const SET_UPLOAD_URL = 'SET_UPLOAD_URL'

const LOGIN = 'LOGIN'
const LOGIN_COMPLETE = 'LOGIN_COMPLETE'
const LOGIN_FAILED = 'LOGIN_FAILED'
const LOGOUT = 'LOGOUT'
const SET_VERSION = 'SET_VERSION'
const ACCESS_TOKEN_KEY = 'access_token'
const USER_PROFILE_KEY = 'user'

const defaultProfile = {
  date_of_birth: null,
  education: null,
  email: null,
  ethnicity: null,
  family_name: null,
  given_name: null,
  language: null,
  lifestyle_domain: null,
  organization: null,
  picture: null,
  sex: null,
  sub: null,
  user_id: null
}

export default {
  namespaced: true,
  state: () => ({
    profile: { ...defaultProfile },
    hasCompleteProfile: false,
    logging_in: false,
    login_error: null,
    version: null,
    meta: {
      uuid: null,
      completion_code: null,
      uploadURL: null
    }
  }),
  getters: {
    isLoggedIn: state => Boolean(state.profile?.user_id)
  },
  mutations: {
    [INIT](state, userInfo) {
      state.profile = { ...userInfo }
      const { sex, date_of_birth } = userInfo
      state.hasCompleteProfile = !!(sex && date_of_birth)
    },
    [SET_VERSION](state, { ver }) {
      state.version = ver
    },
    [INIT_USER](state) {
      const id = uuid()
      state.meta.uuid = id
      state.meta.completion_code = id.slice(-8)
    },
    [SET_PROFILE](state, userInfo) {
      state.profile = { ...userInfo }
      const { sex, date_of_birth, user_id } = userInfo
      state.hasCompleteProfile = !!(sex && date_of_birth)
      /** Need super-properties like age? */
      if (state.hasCompleteProfile) {
        Analytics.register({ sex })
      }
      Analytics.identify(user_id)
      localStorage.setItem(
        USER_PROFILE_KEY,
        JSON.stringify({ version, ...userInfo })
      )
    },
    [SET_UPLOAD_URL](state, payload) {
      state.meta.uploadURL = payload
    },
    [LOGIN](state) {
      state.logging_in = true
    },
    [LOGIN_COMPLETE](state) {
      state.login_error = false
      state.logging_in = false
    },
    [LOGIN_FAILED](state, err) {
      state.logging_in = false
      state.login_error = err
    },
    [LOGOUT](state) {
      API.auth.logout(localStorage.getItem('access_token'))
      localStorage.clear()
      cookies.eraseCookie('api_url')
      cookies.eraseCookie('content_root')
      state.profile = { ...defaultProfile }
    }
  },
  actions: {
    initUser({ commit }) {
      commit(INIT_USER)
    },
    async saveProfile({ commit, state }, payload) {
      const { user_id } = state.profile
      const {
        date_of_birth,
        education,
        ethnicity,
        family_name,
        given_name,
        sex
      } = payload
      try {
        await API.profile.updateProfile(user_id, {
          date_of_birth,
          education,
          ethnicity,
          family_name,
          given_name,
          sex
        })
        commit(SET_PROFILE, payload)
      } catch (e) {
        /** @todo - do something with profile update error? */
      }
    },
    async requestUploadURL({ commit, state }, payload) {
      commit('setPending', true, { root: true })
      s3Client
        .requestUploadURLoadURL(state.meta.uuid, payload.exam)
        .then(url => {
          commit(SET_UPLOAD_URL, url)
          Promise.resolve(url)
        })
        .catch(err => {
          // TODO determine what we do on HTTP errors with design ~JB
          window.console.error(err)
          Promise.reject(err)
        })
        .finally(() => {
          commit('setPending', false, { root: true })
        })
    },
    async login({ commit, dispatch }, payload) {
      commit(LOGIN)
      dispatch('activities/resetActivities', undefined, { root: true })
      let error
      const res = await API.auth
        .login(payload.email, payload.password)
        .catch(ex => { error = ex })
      if (error) {
        commit(LOGIN_FAILED, error)
        return LOGIN_FAILED
      }

      setRefreshFailureHandler(() => dispatch('logout'))

      const { access_token, refresh_token, id_token } = res
      const { username: id } = parseToken(access_token)

      // save the API url to local storage
      saveCustomTokenKey('api_url', id_token)
      saveCustomTokenKey('content_root', id_token)
      // set the API url to use for subsequent API calls
      setHttpBaseUrl()

      // TODO: initialize http things rather than expect passive handoff
      window.localStorage.setItem('access_token', access_token)
      window.localStorage.setItem('refresh_token', refresh_token)

      const data = await API.profile.getProfile(id)
      LogRocketUtil.identify(data.user_id)

      commit(SET_PROFILE, {
        date_of_birth: data.date_of_birth,
        education: data.education,
        email: data.email,
        ethnicity: data.ethnicity,
        family_name: data.family_name,
        given_name: data.given_name,
        language: data.language,
        lifestyle_domain: data.lifestyle_domain,
        organization: data.organization,
        picture: data.picture,
        sex: data.sex,
        sub: data.sub,
        user_id: data.user_id
      })
      commit(LOGIN_COMPLETE)

      return true
    },

    async attemptUserRestore({ commit, dispatch }) {
      try {
        if (cookies.getCookie('legacy_login')) {
          const legacyCookie = JSON.parse(cookies.getCookie('legacy_login'))
          localStorage.setItem(USER_PROFILE_KEY, JSON.stringify(legacyCookie.user.profile))
          localStorage.setItem('refresh_token', legacyCookie.refresh_token)
          localStorage.setItem('access_token', legacyCookie.access_token)
          cookies.eraseCookie('legacy_login')
        }

        if (localStorage.getItem(USER_PROFILE_KEY)) {
          const store = JSON.parse(localStorage.getItem(USER_PROFILE_KEY))
          if (store.version === version) {
            delete store.version
            console.info(`restoring 🧠 '${store.user_id}'`)
            setRefreshFailureHandler(() => dispatch('logout'))
            commit(INIT, { ...store })
          } else {
            console.info(`set app version to:${version}`, store)
            commit(SET_VERSION, { version })
          }
        }
      } catch (e) {} // eslint-disable-line no-empty
    },
    async logout({ commit }, payload) {
      commit(LOGOUT)
      const routeObj = { name: 'Login' }
      // if user is being logged because of inactivity, set params to show alert on Login.vue
      if (payload?.inactivityLogout) {
        routeObj.params = { inactivityLogout: true }
      }
      router.push(routeObj)
    }
  }
}
